import { Button, Card, Toggle, useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, Shield } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { DataCollectionResponse } from "@/api";
import { errorMessage, getDataSourceQueryKey, listDataSourcesQueryKey, updateDataSourceMutation } from "@/api";
import { Text } from "@/components/Text";
import { useCapabilities } from "@/features/account/useCurrentUser";
import { AutomatedSyncConsent } from "./AutomatedSyncConsent";

const REAUTH_MESSAGE = "Automated sync requires re-authorization.";

/**
 * The automated-sync consent control for a SharePoint source (Content tab):
 * a toggle that turns background sync on/off, gated behind a consent dialog
 * when enabling, plus a re-authorize state when the cached permission lapses.
 * Mirrors the old UI's AutomatedSyncControl. Gated on the
 * `automatedSyncEnabled` capability + SharePoint-only.
 */
export function AutomatedSyncCard({ source }: Readonly<{ source: DataCollectionResponse }>) {
  const { t } = useTranslation("dataSources");
  const toast = useToast();
  const queryClient = useQueryClient();
  const capabilities = useCapabilities();
  const [showConsent, setShowConsent] = useState(false);

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: getDataSourceQueryKey({ path: { collection_uuid: source.uuid } }) }),
      queryClient.invalidateQueries({ queryKey: listDataSourcesQueryKey() }),
    ]);

  const apply = useMutation({
    ...updateDataSourceMutation(),
    onSuccess: async (_data, vars) => {
      await invalidate();
      const enabled = Boolean(vars.body.enableAutomatedSync);
      toast.success(enabled ? t("sync.enabled") : t("sync.disabled"));
      setShowConsent(false);
    },
    onError: (e) => toast.error(errorMessage(e, t("sync.toggleFailed"))),
  });

  // Backend-gated + SharePoint-only (matches the old UI's control).
  if (!capabilities.automatedSyncEnabled || source.sourceType !== "sharepoint") return null;

  const enabled = Boolean(source.automatedSyncEnabled);
  const needsReauth = source.status === "error" && source.errorMessage === REAUTH_MESSAGE;
  const ownerLabel = source.syncOwnerName || source.syncOwnerEmail || null;

  // Turning on needs consent first; turning off applies immediately.
  const disable = () => apply.mutate({ path: { collection_uuid: source.uuid }, body: { enableAutomatedSync: false } });
  const askConsent = () => setShowConsent(true);

  return (
    <Card>
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-2">
          <Shield size={16} aria-hidden className="mt-0.5 shrink-0 text-content-tertiary" />
          <div className="min-w-0">
            <Text variant="body">{t("sync.automated")}</Text>
            <Text variant="body-sm" tone="secondary">
              {t("sync.automatedBody")}
            </Text>
            {enabled && ownerLabel && (
              <Text variant="caption" tone="tertiary">
                {t("sync.syncedAs", { owner: ownerLabel })}
              </Text>
            )}
            {needsReauth && (
              <Text variant="caption" tone="warning" className="mt-1">
                {t("sync.reauthNote")}
              </Text>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {needsReauth && (
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<RefreshCw size={14} />}
              onClick={() => setShowConsent(true)}
              disabled={apply.isPending}
            >
              {t("sync.reauthorize")}
            </Button>
          )}
          <Toggle
            label=""
            aria-label={t("sync.automated")}
            checked={enabled}
            onChange={enabled ? disable : askConsent}
          />
        </div>
      </div>

      <AutomatedSyncConsent
        open={showConsent}
        busy={apply.isPending}
        onCancel={() => setShowConsent(false)}
        onAuthorize={() =>
          apply.mutate({ path: { collection_uuid: source.uuid }, body: { enableAutomatedSync: true } })
        }
      />
    </Card>
  );
}
